import React from 'react';
import styles from './PrintPetMedicalConditions.module.scss';
import Pet from '@spike/pet-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faKidneys,
    faVial,
    faBone,
    faViruses,
    faPaw,
    faScalpel,
    faBrain,
    faEar,
    faHeart,
    faLungs,
    faCircleInfo
} from '@fortawesome/pro-light-svg-icons';
import { PetMedicalCondition } from '@spike/medical-history-model';

type Props = {
    pet: Pet;
};

const PrintPetMedicalConditions: React.FC<Props> = ({ pet }) => {
    const conditions = pet?.medicalHistory?.medicalConditions ?? [];

    // Group by category
    const groupedConditions = conditions.reduce((acc: any, condition: PetMedicalCondition) => {
        const categoryName = condition.categoryName || 'Other';
        if (!acc[categoryName]) acc[categoryName] = [];
        acc[categoryName].push(condition);
        return acc;
    }, {});

    const getCategoryIcon = (categoryName: string) => {
        switch (categoryName) {
            case 'Internal Organs':
                return faKidneys;
            case 'Endocrine':
                return faVial;
            case 'Orthopedic':
                return faBone;
            case 'Autoimmune Disorders':
                return faViruses;
            case 'Skin':
                return faPaw;
            case 'Surgery':
                return faScalpel;
            case 'Neurological':
                return faBrain;
            case 'Senses':
                return faEar;
            case 'Heart':
                return faHeart;
            case 'Respiratory':
                return faLungs;
            default:
                return faCircleInfo;
        }
    };

    if (conditions.length === 0) {
        return (
            <div className={styles.section}>
                <div className={styles.sectionTitle}>Medical Conditions</div>
                <div className={styles.empty}>No medical conditions added yet.</div>
            </div>
        );
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionTitle}>Medical Conditions</div>
            <div className={styles.conditionsList}>
                {Object.entries(groupedConditions).map(([categoryName, conditions]: [string, any]) => (
                    <div
                        key={categoryName}
                        className={styles.categorySection}
                    >
                        {/* Header category */}
                        <div className={styles.categoryHeader}>
                            <FontAwesomeIcon
                                icon={getCategoryIcon(categoryName)}
                                className={styles.categoryIcon}
                            />
                            <span className={styles.categoryName}>{categoryName}</span>
                        </div>

                        {conditions.map((condition: PetMedicalCondition, idx: number) => (
                            <div
                                key={condition.id ?? idx}
                                className={styles.conditionItem}
                            >
                                <div className={styles.conditionName}>{condition.conditionName}</div>

                                <div className={styles.metaGroup}>
                                    <label>DESCRIPTION</label>
                                    <p>{condition.explanation || '-'}</p>
                                </div>

                                <div className={styles.metaGroup}>
                                    <label>MEDICATION</label>
                                    <p>{condition.medications || '-'}</p>
                                </div>

                                <div className={styles.metaGroup}>
                                    <label>DIAGNOSIS</label>
                                    <p className={styles.lastMeta}>{condition.howLongAgo?.name || '-'}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PrintPetMedicalConditions;
